import { useDispatch, useSelector } from "react-redux";
import { adwAddFields } from "../../dataWranglerSlice";
import {
    ChangeEvent,
    ForwardedRef,
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState
} from "react";
import { useADWTableDefinition } from "../../hooks/useADWTableDefinition";
import { AgGridReact } from "@ag-grid-community/react";
import { Box, Button, Chip, Stack, TextField, Tooltip } from '@mui/material';
import { selectSelectedDatabase } from '../../../common/databaseSlice';
import { useGetDataDictionaryMutation, useGetSelectedFieldsMutation } from "../../../../api/dataDictionaryApiSlice";
import { IRowNode, IServerSideGetRowsParams, RowDragEndEvent, SideBarDef, IRowDragItem, SortModelItem } from "@ag-grid-community/core";
import { ServerSideRowSelectionState } from "@ag-grid-community/core/dist/types/src/interfaces/selectionState";
import type { GetRowIdParams } from "@ag-grid-community/core/dist/types/src/interfaces/iCallbackParams";
import { useADWFilterHook } from "../../hooks/useADWFilterHook";
import CloseIcon from "@mui/icons-material/Close";
import useDebounce from "../../../../hooks/useDebounce";

interface Props {
    dragZone: HTMLElement | null;
    setSearchText: (value: string) => void,
    updateSortModel: (value: SortModelItem[]) => void,
    searchText: string;
}

interface TooltipFilter {
    filterType: string;
    values: string[];
    filter?: string;
}

const ADWGrid = forwardRef(function ADWGrid(props: Props, outerGridRef: ForwardedRef<AgGridReact>) {
    const { dragZone, updateSortModel, setSearchText, searchText } = props;
    const [value, setValue] = useState<string>(searchText ?? "");
    const debouncedValue = useDebounce<string>(value, 500);
    const searchTextRef = useRef<string>(searchText ?? "");

    const gridRef = useRef<AgGridReact>(null);
    const { activeFilters, onFilterChange, clearAllFilters, getFieldFilters, clearFieldFilter } = useADWFilterHook();
    useImperativeHandle(outerGridRef, () => gridRef.current!, []);

    const dispatch = useDispatch();
    const selectedDatabase = useSelector(selectSelectedDatabase);
    const tableDefinition = useADWTableDefinition({ getFieldFilters });

    const [triggerFetch] = useGetDataDictionaryMutation();
    const [triggerFetchSelected] = useGetSelectedFieldsMutation();

    const fetchRows = useCallback(async (requestParams: IServerSideGetRowsParams) => {
        const { success, fail, request } = requestParams;
        updateSortModel(request.sortModel)
        try {
            const data = await triggerFetch({
                request,
                database: selectedDatabase,
                search: searchTextRef.current ?? ""
            }).unwrap();
            success({
                rowData: data.rows,
                rowCount: data.totalRows,
            });
        } catch (err) {
            console.error("Failed to fetch rows", err);
            fail();
        }
    }, [triggerFetch, selectedDatabase, updateSortModel]);

    useEffect(() => {
        setSearchText(debouncedValue);
        searchTextRef.current = debouncedValue;
    }, [debouncedValue]);

    useEffect(() => {
        if (gridRef.current && gridRef.current.api) {
            gridRef?.current.api.closeToolPanel();
            gridRef?.current?.api.refreshServerSide({ purge: true });
        }
    }, [clearAllFilters, searchTextRef.current, selectedDatabase]);

    const onGridReady = useCallback((params: any) => {
        params.api.closeToolPanel();
        params.api.setGridOption("serverSideDatasource", { getRows: fetchRows });

        const dropZone = {
            getContainer: () => dragZone,
            onDragStop: async (param: RowDragEndEvent) => {
                const serverSelectAll: ServerSideRowSelectionState = param.api?.getServerSideSelectionState() as ServerSideRowSelectionState;
                if (serverSelectAll.selectAll) {
                    const selected = await triggerFetchSelected({
                        database: selectedDatabase,
                        search: searchTextRef.current ?? "",
                        filterModel: param.api.getFilterModel(),
                        excluded: serverSelectAll.toggledNodes
                    }).unwrap();
                    dispatch(adwAddFields(selected));
                } else {
                    const dropped = param.nodes.map((node: IRowNode) => {
                        return {
                            id: node.data.id,
                            table_label: node.data.table_label,
                            table_name: node.data.table_name,
                            name: node.data.name
                        }
                    });
                    dispatch(adwAddFields(dropped));
                }
                params.api.deselectAll();
            }
        };
        params.api.addRowDropZone(dropZone);
    }, [dragZone, dispatch, fetchRows]);

    const rowDragText = (params: IRowDragItem, dragItemCount: number) => {
        const serverSelectAll: ServerSideRowSelectionState = gridRef.current?.api?.getServerSideSelectionState() as ServerSideRowSelectionState;
        const count = serverSelectAll?.selectAll ?
            gridRef.current?.api.paginationGetRowCount() as number - serverSelectAll.toggledNodes.length
            : dragItemCount;
        return `${count} ${count === 1 ? "item" : "items"} selected.`;
    }

    const extracted = (field: string, filter: TooltipFilter) => {
        if (filter.filterType === 'text') return { tooltipLabel: `${field}: ${filter.filter}`, label: filter.filter };

        const tooltipFilterOptionsText = filter?.values?.join(", ")
        const tooltipLabel = `${field}: ${tooltipFilterOptionsText.length >= 2000 ? tooltipFilterOptionsText.slice(0, 2000) + "..." : tooltipFilterOptionsText}`;
        const label = getTooltipLabel(field, filter);
        return { tooltipLabel, label };
    }

    const tabularParams = {
        columnKeys: gridRef?.current?.api?.getAllDisplayedColumns()
            .filter((column) => column.getColId() !== "ag-Grid-ControlsColumn"),
        processCellCallback: function (cell: any) {
            if (typeof cell.value === 'object' && cell.value !== null) {
                return JSON.stringify(cell.value, null, 2);
            }
            return cell.value;
        }
    };

    const sideBar: SideBarDef = useMemo(() => {
        return {
            toolPanels: [
                {
                    id: 'columns',
                    labelDefault: 'Columns',
                    labelKey: 'columns',
                    iconKey: 'columns',
                    toolPanel: 'agColumnsToolPanel',
                    minWidth: 225,
                    maxWidth: 225,
                    width: 225,
                    toolPanelParams: {
                        suppressRowGroups: true,
                        suppressValues: true,
                    }
                },
                {
                    id: 'filters',
                    labelDefault: 'Filters',
                    labelKey: 'filters',
                    iconKey: 'filter',
                    toolPanel: 'agFiltersToolPanel',
                    minWidth: 180,
                    maxWidth: 400,
                    width: 250
                }
            ],
            defaultToolPanel: 'filters',
            position: 'right',
        };
    }, []);

    function getTooltipLabel(field: string, filter: TooltipFilter) {
        const quantityFilter = filter.values.length;
        if (quantityFilter <= 0) {
            return `No ${field} selected`;
        }

        if (quantityFilter > 3) {
            return filter.values.slice(0, 1).join(", ") + ` + ${filter.values.length - 1} items`;
        }

        return filter.values.join(", ");
    }

    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        setValue(event.target.value);
    };

    return <>
        <Stack gap={2} style={{ height: '100%', display: 'flex', width: '100%' }}>
            <TextField
                sx={{ minWidth: 100 }}
                variant="outlined" size="small" label="Search"
                value={value} onChange={handleChange}
                InputProps={{
                    endAdornment: <CloseIcon sx={{ cursor: 'pointer' }} onClick={() => setValue("")}/>,
                }}
            />
            {Object.entries(activeFilters).length > 0 &&
                <Box
                    sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                        flexGrow: 1,
                        justifyContent: 'space-between',
                    }}
                >
                    <Stack direction="row" spacing={1} sx={{ width: '85%', overflowX: 'scroll', scrollbarWidth: 'thin' }}>
                        {Object.entries(activeFilters).map(([field, filter]) => {
                            const { tooltipLabel, label } = extracted(field, filter);
                            return (
                                <Tooltip key={field} title={tooltipLabel} arrow>
                                    <Chip
                                        id={`filter-chip-${field}`}
                                        label={`${field}: ${label}`}
                                        onDelete={() => clearFieldFilter(field, gridRef.current)}
                                        color="primary"
                                        variant="outlined"
                                        sx={{ flexShrink: 0 }}
                                    />
                                </Tooltip>
                            )
                        })}
                    </Stack>
                    <Button onClick={() => clearAllFilters(gridRef.current)} variant="outlined" sx={{ height: '32px', flexShrink: 0 }}>
                        Clear all filters
                    </Button>
                </Box>
            }

            <div
                className="ag-theme-material ag-theme-common ag-theme-acmecorp"
                style={{ height: '100%', width: '100%' }}
            >
                <AgGridReact
                    {...props}
                    ref={gridRef}
                    rowModelType="serverSide"
                    serverSideEnableClientSideSort={true}
                    onGridReady={onGridReady}
                    columnDefs={tableDefinition.columns}
                    defaultColDef={tableDefinition.default}
                    onFilterChanged={onFilterChange}

                    sideBar={sideBar}
                    pagination={true}
                    paginationPageSize={10}
                    cacheBlockSize={1000}
                    paginationPageSizeSelector={[10, 20, 50, 100, 1000]}
                    serverSideInitialRowCount={10}
                    allowShowChangeAfterFilter={true}

                    suppressCellFocus={true}
                    alwaysMultiSort={true}
                    rowDragMultiRow={true}
                    rowDragEntireRow={true}
                    rowDragText={rowDragText}
                    rowSelection={{
                        mode: "multiRow",
                        headerCheckbox: true,
                        checkboxes: true,
                        selectAll: 'filtered'
                    }}
                    getRowId={(row: GetRowIdParams) => `${row.data.id}`}
                    getRowStyle={() => ({ cursor: 'grabbing' })}
                    defaultCsvExportParams={tabularParams}
                    defaultExcelExportParams={tabularParams}
                />
            </div>
        </Stack>
    </>
});

export default ADWGrid;
